<template>
    <div class="container">
        <div class="pt-5">
            <h1 class="h3">Settings</h1>
            <div class="py-2">
                <settings-menu></settings-menu>
            </div>
            <div class="row py-3 border-bottom">
                <div class="col-md-6 col-sm-12">
                    <h3 class="h3 text-primary">Feature settings</h3>
                    <small>Every feature toggle in one place. Premium modules are billed on top of your plan — the badge shows the plan tier they require.</small>
                </div>
            </div>

            <div class="container text-left mb-5" v-if="companyDetails.companysettings">

                <h5 class="pt-4 pb-1">Premium modules</h5>
                <div class="row py-3 border-bottom" v-for="feature in premiumFeatures" :key="feature.key">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">{{ feature.title }}</h6>
                        <small>{{ feature.description }}</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="d-flex align-items-center" style="gap:10px;">
                            <div class="custom-control custom-switch">
                                <input type="checkbox" class="custom-control-input"
                                    :id="feature.key"
                                    :checked="companyDetails.companysettings[feature.key]"
                                    :disabled="togglingFeature === feature.key"
                                    @change="onAdvancedFeatureToggle($event, feature.key)">
                                <label class="custom-control-label" :for="feature.key"><small>Enable</small></label>
                            </div>
                            <span class="badge badge-secondary">KES {{ feature.amount.toLocaleString() }}/mo</span>
                        </div>
                        <div v-if="togglingFeature === feature.key" class="mt-1">
                            <small class="text-muted"><span class="spinner-border spinner-border-sm mr-1"></span>Saving…</small>
                        </div>
                    </div>
                </div>

                <div class="row py-3 border-bottom" v-if="companyDetails.companysettings.has_subscriptions">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Gym / Subscriptions only</h6>
                        <small>This business runs on memberships, not POS sales. Home opens Members, and the menu shows Front desk and Packages instead of Orders and Inventory. Your data is not changed.</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input"
                                id="primary_use_case_subscriptions"
                                :checked="companyDetails.companysettings.primary_use_case === 'subscriptions'"
                                :disabled="togglingPrimaryUseCase"
                                @change="setPrimaryUseCase($event.target.checked ? 'subscriptions' : null, true)">
                            <label class="custom-control-label" for="primary_use_case_subscriptions"><small>Enable</small></label>
                        </div>
                    </div>
                </div>

                <div class="row py-3 border-bottom" v-if="companyDetails.companysettings.has_reservations">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Hotel / BnB only</h6>
                        <small>This business takes room bookings, not POS sales. Home opens Today (arrivals, departures, rooms), and the menu shows Rooms and Bookings instead of Orders. Your data is not changed.</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input"
                                id="primary_use_case_reservations"
                                :checked="companyDetails.companysettings.primary_use_case === 'reservations'"
                                :disabled="togglingPrimaryUseCase"
                                @change="setPrimaryUseCase($event.target.checked ? 'reservations' : null, true)">
                            <label class="custom-control-label" for="primary_use_case_reservations"><small>Enable</small></label>
                        </div>
                    </div>
                </div>

                <h5 class="pt-4 pb-1">Invoicing &amp; Quotes</h5>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Invoices &amp; Quotes</h6>
                        <small>Create quotes and invoices for customers, track payments, and convert quotes into invoices. Free on every plan.</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input"
                                id="has_invoices"
                                :checked="companyDetails.companysettings.has_invoices"
                                :disabled="togglingSimple === 'has_invoices'"
                                @change="onInvoicingToggle($event)">
                            <label class="custom-control-label" for="has_invoices"><small>Enable</small></label>
                        </div>
                    </div>
                </div>
                <div class="row py-3 border-bottom" v-if="companyDetails.companysettings.has_invoices">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Invoicing &amp; Quotes only</h6>
                        <small>This business only needs Invoicing &amp; Quotes — not a POS. Switches your Home and navigation to the invoicing dashboard.</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input"
                                id="primary_use_case_invoices_quotes"
                                :checked="companyDetails.companysettings.primary_use_case === 'invoices_quotes'"
                                :disabled="togglingPrimaryUseCase"
                                @change="onPrimaryUseCaseToggle($event)">
                            <label class="custom-control-label" for="primary_use_case_invoices_quotes"><small>Enable</small></label>
                        </div>
                        <div v-if="togglingPrimaryUseCase" class="mt-1">
                            <small class="text-muted"><span class="spinner-border spinner-border-sm mr-1"></span>Saving…</small>
                        </div>
                    </div>
                </div>

                <h5 class="pt-4 pb-1">Sales &amp; pricing</h5>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Taxes and VAT</h6>
                        <small>This will be added on every sale. <a href="/products">To update go to inventory and change for each item</a></small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.enable_taxes" :disabled="togglingSimple === 'enable_taxes'" @change="onSimpleFeatureToggle($event, 'enable_taxes')" id="all-taxes">
                            <label class="custom-control-label" for="all-taxes"><small>Enable Vat &amp; other taxes</small></label>
                        </div>
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Product discounts</h6>
                        <small>Show product-level discounts on receipts</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.enable_discounts" :disabled="togglingSimple === 'enable_discounts'" @change="onSimpleFeatureToggle($event, 'enable_discounts')" id="discounts">
                            <label class="custom-control-label" for="discounts"><small>Enable product discounts</small></label>
                        </div>
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Order discount</h6>
                        <small>Allow cashiers to apply a manual discount to the full order total at checkout</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.enable_order_discount" :disabled="togglingSimple === 'enable_order_discount'" @change="onSimpleFeatureToggle($event, 'enable_order_discount')" id="enable_order_discount">
                            <label class="custom-control-label" for="enable_order_discount"><small>Allow order discount</small></label>
                        </div>
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Discount campaigns</h6>
                        <small>Enable time-bound promotional pricing campaigns for products</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.enable_product_discount_campaigns" :disabled="togglingSimple === 'enable_product_discount_campaigns'" @change="onSimpleFeatureToggle($event, 'enable_product_discount_campaigns')" id="enable_product_discount_campaigns">
                            <label class="custom-control-label" for="enable_product_discount_campaigns"><small>Enable discount campaigns</small></label>
                        </div>
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Enable Wholesale</h6>
                        <small>Allow selling of goods and services with wholesale prices</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.has_wholesale" :disabled="togglingSimple === 'has_wholesale'" @change="onSimpleFeatureToggle($event, 'has_wholesale')" id="has-wholesale">
                            <label class="custom-control-label" for="has-wholesale"><small>Enable</small></label>
                        </div>
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Price change on the go</h6>
                        <small class="text-danger">Allows one to change prices of items when making sales.</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.allows_price_change_on_the_go" :disabled="togglingSimple === 'allows_price_change_on_the_go'" @change="onSimpleFeatureToggle($event, 'allows_price_change_on_the_go')" id="price-change-on-the-go">
                            <label class="custom-control-label" for="price-change-on-the-go"><small>Enable Selling Price Change</small></label>
                        </div>
                    </div>
                </div>
                <div v-if="companyDetails.companysettings.allows_price_change_on_the_go" class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Selling Price cannot go below Wholesale Price</h6>
                        <small class="text-danger">This will lock the lowest selling price to the wholesale selling price.</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.price_not_go_below_wholesale" :disabled="togglingSimple === 'price_not_go_below_wholesale'" @change="onSimpleFeatureToggle($event, 'price_not_go_below_wholesale')" id="price-not-below-wholesale-bp">
                            <label class="custom-control-label" for="price-not-below-wholesale-bp"><small>Enable</small></label>
                        </div>
                    </div>
                </div>

                <h5 class="pt-4 pb-1">Inventory &amp; fulfillment</h5>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Stock Buffer</h6>
                        <small>Allow selling items even if they are below Zero</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.has_stock_buffer" :disabled="togglingSimple === 'has_stock_buffer'" @change="onSimpleFeatureToggle($event, 'has_stock_buffer')" id="stock-buffer">
                            <label class="custom-control-label" for="stock-buffer"><small>Enable</small></label>
                        </div>
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Borrowed Stock</h6>
                        <small>Let cashiers borrow stock from a supplier or shop to fulfil a sale when a product is out of stock. Works even if Stock Buffer above is on — cashiers can choose to borrow (tracked) instead of letting stock go negative untracked.</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.allow_borrowed_stock" :disabled="togglingSimple === 'allow_borrowed_stock'" @change="onSimpleFeatureToggle($event, 'allow_borrowed_stock')" id="allow-borrowed-stock">
                            <label class="custom-control-label" for="allow-borrowed-stock"><small>Enable</small></label>
                        </div>
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Enable Two Step Verification for Orders</h6>
                        <small>Double Verify Orders. This apply mostly for items/services that is paid and will be collected later</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.has_order_verification" :disabled="togglingSimple === 'has_order_verification'" @change="onSimpleFeatureToggle($event, 'has_order_verification')" id="has-order-verification">
                            <label class="custom-control-label" for="has-order-verification"><small>Enable</small></label>
                        </div>
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Enable Pack / Dispatch for POS orders</h6>
                        <small>Require items to be packed and dispatched before stock is deducted, same as Shopify/WooCommerce orders.</small>
                        <small v-if="channelIntegrationConnected" class="d-block text-muted mt-1">Automatically enabled because Shopify or WooCommerce is connected — those orders always use Pack / Dispatch.</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input"
                                :checked="companyDetails.companysettings.enable_fulfillment_dispatch || channelIntegrationConnected"
                                :disabled="channelIntegrationConnected || togglingSimple === 'enable_fulfillment_dispatch'"
                                @change="onSimpleFeatureToggle($event, 'enable_fulfillment_dispatch')"
                                id="enable-fulfillment-dispatch">
                            <label class="custom-control-label" for="enable-fulfillment-dispatch"><small>Enable</small></label>
                        </div>
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Enable Returns for POS orders</h6>
                        <small>Record physical returns and restore stock for dispatched POS orders. Independent of Pack / Dispatch above.</small>
                        <small v-if="channelIntegrationConnected" class="d-block text-muted mt-1">Automatically enabled because Shopify or WooCommerce is connected — those orders always support Returns.</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input"
                                :checked="companyDetails.companysettings.enable_fulfillment_returns || channelIntegrationConnected"
                                :disabled="channelIntegrationConnected || togglingSimple === 'enable_fulfillment_returns'"
                                @change="onSimpleFeatureToggle($event, 'enable_fulfillment_returns')"
                                id="enable-fulfillment-returns">
                            <label class="custom-control-label" for="enable-fulfillment-returns"><small>Enable</small></label>
                        </div>
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Enable Courier Tracking</h6>
                        <small>Capture a courier tracking number on Pack / Dispatch for orders being sent to customers.</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.enable_courier_tracking" :disabled="togglingSimple === 'enable_courier_tracking'" @change="onSimpleFeatureToggle($event, 'enable_courier_tracking')" id="enable-courier-tracking">
                            <label class="custom-control-label" for="enable-courier-tracking"><small>Enable</small></label>
                        </div>
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Items can have images</h6>
                        <small>On the make sales page item images can be displayed</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.items_have_images" :disabled="togglingSimple === 'items_have_images'" @change="onSimpleFeatureToggle($event, 'items_have_images')" id="items-image">
                            <label class="custom-control-label" for="items-image"><small>Enable</small></label>
                        </div>
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Items have expiry date</h6>
                        <small>You can enable this and edit expiry date per item in <a href="/inventory">inventory</a></small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.expiry_date" :disabled="togglingSimple === 'expiry_date'" @change="onSimpleFeatureToggle($event, 'expiry_date')" id="expiry-date">
                            <label class="custom-control-label" for="expiry-date"><small>Enable</small></label>
                        </div>
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Stock Alerts</h6>
                        <small>Get notifications when stock is low (This is only email and SMS)</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.stock_alert" :disabled="togglingSimple === 'stock_alert'" @change="onSimpleFeatureToggle($event, 'stock_alert')" id="stock-alert">
                            <label class="custom-control-label" for="stock-alert"><small>Enable</small></label>
                        </div>
                    </div>
                </div>

                <h5 class="pt-4 pb-1">AI</h5>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">SharaGPT</h6>
                        <small>Enable SharaGPT for this business. SharaGPT provides AI-powered assistance using your Dukakit business data.</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.sharagpt_enabled" :disabled="togglingSimple === 'sharagpt_enabled'" @change="onSimpleFeatureToggle($event, 'sharagpt_enabled')" id="sharagpt-enabled">
                            <label class="custom-control-label" for="sharagpt-enabled"><small>Enabled / Disabled</small></label>
                        </div>
                    </div>
                </div>

                <h5 class="pt-4 pb-1">Employee permissions</h5>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Show stock</h6>
                        <small>Allow employees to see stock count on the <a href="/makesale">make sale page</a></small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.allow_employees_see_stock_count" :disabled="togglingSimple === 'allow_employees_see_stock_count'" @change="onSimpleFeatureToggle($event, 'allow_employees_see_stock_count')" id="see-stock">
                            <label class="custom-control-label" for="see-stock"><small>Enable</small></label>
                        </div>
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Allow employees to see payment summaries</h6>
                        <small>This will show summary based on payment method on orders page</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.allow_summaries" :disabled="togglingSimple === 'allow_summaries'" @change="onSimpleFeatureToggle($event, 'allow_summaries')" id="see_allow_summaries">
                            <label class="custom-control-label" for="see_allow_summaries"><small>Enable</small></label>
                        </div>
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Allow employees to see sales analysis</h6>
                        <small>Show the Daily Sales Analysis button to non-admin employees on the orders page</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.allows_employees_see_analysis" :disabled="togglingSimple === 'allows_employees_see_analysis'" @change="onSimpleFeatureToggle($event, 'allows_employees_see_analysis')" id="allows_employees_see_analysis">
                            <label class="custom-control-label" for="allows_employees_see_analysis"><small>Enable</small></label>
                        </div>
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Employees can delete pending orders</h6>
                        <small>Allow employees to see delete orders that are pending</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.allow_employees_delete_orders" :disabled="togglingSimple === 'allow_employees_delete_orders'" @change="onSimpleFeatureToggle($event, 'allow_employees_delete_orders')" id="delete-orders">
                            <label class="custom-control-label" for="delete-orders"><small>Enable</small></label>
                        </div>
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Hide all orders</h6>
                        <small>Employees with no rights to manage orders wont be able to see sales orders</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.hide_orders_from_employees" :disabled="togglingSimple === 'hide_orders_from_employees'" @change="onSimpleFeatureToggle($event, 'hide_orders_from_employees')" id="cant-see-orders">
                            <label class="custom-control-label" for="cant-see-orders"><small>Enable</small></label>
                        </div>
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">User Pin</h6>
                        <small class="text-danger">Employees can switch to another account on make sale page</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.users_pin_login" :disabled="togglingSimple === 'users_pin_login'" @change="onSimpleFeatureToggle($event, 'users_pin_login')" id="account-switch">
                            <label class="custom-control-label" for="account-switch"><small>Enable account switching</small></label>
                        </div>
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Track employee attendance</h6>
                        <small>Shows employees a "Checked in at" indicator once their first activity of the day is recorded.</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.track_employee_attendance" :disabled="togglingSimple === 'track_employee_attendance'" @change="onSimpleFeatureToggle($event, 'track_employee_attendance')" id="track-employee-attendance">
                            <label class="custom-control-label" for="track-employee-attendance"><small>Enable</small></label>
                        </div>
                    </div>
                </div>

                <h5 class="pt-4 pb-1">Checkout &amp; orders</h5>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Skip Checkout done page</h6>
                        <small>On validating payment go directly to print receipt page</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.skip_checkout_done" :disabled="togglingSimple === 'skip_checkout_done'" @change="onSimpleFeatureToggle($event, 'skip_checkout_done')" id="skip_checkout_done">
                            <label class="custom-control-label" for="skip_checkout_done"><small>Enable</small></label>
                        </div>
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Appointments</h6>
                        <small>To enable Appointments, this is for business that have appointments sessions, like saloons barbershops etc</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.has_appointments" :disabled="togglingSimple === 'has_appointments'" @change="onSimpleFeatureToggle($event, 'has_appointments')" id="has_appointments">
                            <label class="custom-control-label" for="has_appointments"><small>Enable Appointments</small></label>
                        </div>
                    </div>
                </div>

                <h5 class="pt-4 pb-1">Reports &amp; notifications</h5>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Daily Customer SMS</h6>
                        <small>Send automated daily SMS messages to customers.</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.send_messages_daily" :disabled="togglingSimple === 'send_messages_daily'" @change="onSimpleFeatureToggle($event, 'send_messages_daily')" id="send_messages_daily">
                            <label class="custom-control-label" for="send_messages_daily"><small>Enable Daily SMS</small></label>
                        </div>
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Daily Reports</h6>
                        <small>Receive daily sales reports</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.daily_reports" :disabled="togglingSimple === 'daily_reports'" @change="onSimpleFeatureToggle($event, 'daily_reports')" id="daily-reports">
                            <label class="custom-control-label" for="daily-reports"><small>Send me daily reports</small></label>
                        </div>
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Weekly Reports</h6>
                        <small>Receive weekly sales reports (On Monday)</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.weekly_reports" :disabled="togglingSimple === 'weekly_reports'" @change="onSimpleFeatureToggle($event, 'weekly_reports')" id="weekly-report">
                            <label class="custom-control-label" for="weekly-report"><small>Send me weekly reports</small></label>
                        </div>
                    </div>
                </div>
                <div class="row py-3 border-bottom">
                    <div class="col-md-6 col-sm-12">
                        <h6 class="py-1">Monthly Reports</h6>
                        <small>Receive monthly sales reports (On first day of every month)</small>
                    </div>
                    <div class="col-md-6 col-sm-12">
                        <div class="custom-control custom-switch">
                            <input type="checkbox" class="custom-control-input" :checked="companyDetails.companysettings.monthly_reports" :disabled="togglingSimple === 'monthly_reports'" @change="onSimpleFeatureToggle($event, 'monthly_reports')" id="monthly-reports">
                            <label class="custom-control-label" for="monthly-reports"><small>Send me monthly reports</small></label>
                        </div>
                    </div>
                </div>

            </div>
        </div>

        <!-- ── PLAN UPGRADE WARNING MODAL (shared by all premium module toggles) ── -->
        <b-modal
            ref="planWarningModal"
            title="Plan Notice — Feature Requires Upgrade"
            hide-footer
            centered
        >
            <div class="alert alert-warning d-flex align-items-start mb-3">
                <i class="bi bi-exclamation-triangle-fill mr-2 mt-1" style="font-size:1.2rem;"></i>
                <div>
                    <strong>Your current plan may not cover this feature.</strong>
                    <p class="mb-0 mt-1" style="font-size:.9rem;">{{ planWarningMessage }}</p>
                </div>
            </div>
            <p style="font-size:.9rem;">
                Enabling this feature will not immediately change your billing.
                Contact support to upgrade your plan.
            </p>
            <div class="d-flex justify-content-end gap-2 mt-3">
                <button class="btn btn-outline-secondary mr-2" @click="cancelFeatureToggle">
                    Cancel
                </button>
                <button
                    class="btn btn-warning"
                    :disabled="!!togglingFeature"
                    @click="confirmFeatureToggle"
                >
                    <span v-if="togglingFeature" class="spinner-border spinner-border-sm mr-1"></span>
                    Enable Anyway
                </button>
            </div>
        </b-modal>
    </div>
</template>

<script>
    import clearCompanyScopedCache from '../../services/companyCache'

    export default {
        name: "feature-settings",
        data() {
            return {
                companyDetails: {
                    companysettings: {}
                },
                togglingFeature: null,      // gated (plan-enforced) toggle currently saving
                togglingSimple: null,       // simple (ungated) toggle currently saving
                togglingPrimaryUseCase: false,
                planWarningMessage: '',
                pendingFeatureToggle: null,

                // Mirrors PlanEnforcementService::ADVANCED_FEATURES (KES 3,000/mo tier).
                // Keep in sync with app/Services/PlanEnforcementService.php.
                premiumFeatures: [
                    { key: 'has_payroll',       title: 'Payroll',       description: 'Enable the payroll module for processing employee wages, deductions, and attendance.', amount: 3000 },
                    { key: 'has_reservations',  title: 'Reservations',  description: 'This is for hotels which have rooms and booking spaces that need to be reserved.', amount: 3000 },
                    { key: 'has_kds',           title: 'KDS',           description: 'This is applicable to restaurants that need to send orders to kitchen.', amount: 3000 },
                    { key: 'has_subscriptions', title: 'Subscriptions', description: 'Enable the customer subscription module (memberships, gym passes, recurring access, etc.)', amount: 3000 },
                    { key: 'has_manufacturing', title: 'Manufacturing', description: 'Enable the manufacturing module for bill of materials, production orders, and stage tracking.', amount: 3000 },
                    { key: 'has_loyalty',       title: 'Loyalty Program', description: 'Reward customers with points on completed sales, redeemable for a discount at checkout. Configure rates under Settings → Loyalty.', amount: 3000 },
                    { key: 'enable_shifts',     title: 'Shifts',        description: 'Do you have teams working in shifts?', amount: 3000 },
                    { key: 'enable_tims',       title: 'Tims/Etims',    description: 'This will allow Dukakit to configure for your account to use KRA E-tims or Tims.', amount: 3000 },
                    { key: 'has_projects',      title: 'Projects',      description: 'Track customer engagements with a budget, linked invoices/expenses/purchases, and a public link so site managers can request materials without an account.', amount: 3000 },
                ],
            }
        },
        computed: {
            channelIntegrationConnected() {
                const settings = this.companyDetails.companysettings;
                return !!(settings.shopify_enabled || settings.woocommerce_enabled);
            },
        },
        mounted() {
            this.getUserCompanyDetails()
        },
        methods: {
            getUserCompanyDetails() {
                axios.get('/api/user')
                    .then(result => {
                        this.companyDetails = result.data.company
                    }).catch(errors => {
                        console.log(errors.message)
                    });
            },

            // ── Premium modules (plan-enforced) ─────────────────────────────

            onAdvancedFeatureToggle(event, featureKey) {
                const newValue = event.target.checked
                this.pendingFeatureToggle = { key: featureKey, value: newValue }
                this.applyFeatureToggle(featureKey, newValue, false)
            },

            async applyFeatureToggle(featureKey, value, confirmed) {
                this.togglingFeature = featureKey
                try {
                    const { data } = await axios.post('/api/settings/toggle-feature', {
                        feature:   featureKey,
                        value:     value,
                        confirmed: confirmed,
                    })

                    if (data.requires_confirmation) {
                        this.planWarningMessage = data.message
                        this.$refs.planWarningModal.show()
                        return
                    }

                    this.companyDetails.companysettings[featureKey] = data.value
                    this.pendingFeatureToggle = null
                    // BottomMenu.vue and other pages read feature flags from
                    // cached userDetails — clear it so they pick up this
                    // change on next load instead of showing stale nav/UI.
                    clearCompanyScopedCache()
                    this.$toasts.push({ type: 'success', message: data.message })
                } catch (e) {
                    const msg = e.response?.data?.message || 'Failed to update setting.'
                    this.$toasts.push({ type: 'error', message: msg })
                    this.pendingFeatureToggle = null
                } finally {
                    this.togglingFeature = null
                }
            },

            confirmFeatureToggle() {
                this.$refs.planWarningModal.hide()
                if (this.pendingFeatureToggle) {
                    const { key, value } = this.pendingFeatureToggle
                    this.applyFeatureToggle(key, value, true)
                }
            },

            cancelFeatureToggle() {
                this.$refs.planWarningModal.hide()
                this.pendingFeatureToggle = null
            },

            // ── Simple (ungated) feature toggles ────────────────────────────

            async onSimpleFeatureToggle(event, key) {
                const value = event.target.checked
                this.togglingSimple = key
                try {
                    const { data } = await axios.post('/api/settings/toggle-simple-feature', { feature: key, value })
                    this.companyDetails.companysettings[key] = data.value
                    clearCompanyScopedCache()
                    this.$toasts.push({ type: 'success', message: 'Setting updated' })
                } catch (e) {
                    const msg = e.response?.data?.message || 'Failed to update setting.'
                    this.$toasts.push({ type: 'error', message: msg })
                    event.target.checked = !value
                } finally {
                    this.togglingSimple = null
                }
            },

            // Turning invoicing off while "Invoicing & Quotes only" mode is
            // active would strand the company on a nav/dashboard for a
            // feature it no longer has - clear that mode first.
            async onInvoicingToggle(event) {
                const enabling = event.target.checked
                if (!enabling && this.companyDetails.companysettings.primary_use_case === 'invoices_quotes') {
                    await this.setPrimaryUseCase(null, false)
                }
                await this.onSimpleFeatureToggle(event, 'has_invoices')
            },

            // Changes what Home/the bottom nav/side menu show (BottomMenu.vue
            // reads primary_use_case from cached userDetails) - clear that
            // cache and reload so the change is visible immediately, same
            // pattern as services/branchSwitch.js.
            async onPrimaryUseCaseToggle(event) {
                await this.setPrimaryUseCase(event.target.checked ? 'invoices_quotes' : null, true)
            },

            async setPrimaryUseCase(primaryUseCase, reload) {
                this.togglingPrimaryUseCase = true
                try {
                    const { data } = await axios.post('/api/settings/primary-use-case', { primaryUseCase })
                    this.companyDetails.companysettings.primary_use_case = data.primary_use_case
                    clearCompanyScopedCache()
                    window.localStorage.removeItem('userDetails')
                    if (reload) {
                        window.location.reload()
                    }
                } catch (e) {
                    const msg = e.response?.data?.message || 'Failed to update setting.'
                    this.$toasts.push({ type: 'error', message: msg })
                } finally {
                    this.togglingPrimaryUseCase = false
                }
            },
        },
    }
</script>

<style scoped>
</style>
